import React, { useState, useEffect } from "react";
import localScheduleData from "./scheduleData.json";
import "./program.css";
import {
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaUserTie,
  FaUsers,
  FaChalkboardTeacher,
  FaUserMd,
  FaListUl,
} from "react-icons/fa";

const ConferenceSchedule = () => {
  // State to hold the fetched schedule data
  const [scheduleData, setScheduleData] = useState(null);
  const [scheduleError, setScheduleError] = useState("");
  // Track the currently selected session (with its color) for modal popup
  const [selectedSession, setSelectedSession] = useState(null);

  // Define the same colors as in your CSS alternate background colors
  const sessionColors = [
    "#e3f2fd", // light blue
    "#fce4ec", // light pink
    "#e8f5e9", // light green
    "#fff3e0", // light orange
    "#ede7f6", // light purple
    "#f3e5f5", // light violet
  ];

  const isProduction = process.env.NODE_ENV === "production";
  const isTest = process.env.NODE_ENV === "test";
  const useRemoteScheduleInDev =
    process.env.REACT_APP_USE_REMOTE_SCHEDULE_IN_DEV === "true";
  const disableScheduleCache =
    process.env.REACT_APP_DISABLE_SCHEDULE_CACHE !== "false";
  const shouldUseRemoteSchedule =
    isProduction || isTest || useRemoteScheduleInDev;

  useEffect(() => {
    let isMounted = true;
    let activeController = null;

    if (!shouldUseRemoteSchedule) {
      setScheduleError("");
      setScheduleData(localScheduleData);
      return undefined;
    }

    const loadSchedule = async () => {
      setScheduleError("");
      setScheduleData(null);

      for (let attempt = 1; attempt <= 2; attempt += 1) {
        activeController = new AbortController();

        try {
          const scheduleUrl = new URL("https://conference.smdc.edu.pk/scheduleData.json");

          if (disableScheduleCache) {
            scheduleUrl.searchParams.set("_ts", `${Date.now()}`);
          }

          const headers = {
            Accept: "application/json",
          };

          const response = await fetch(scheduleUrl.toString(), {
            cache: disableScheduleCache ? "no-store" : "default",
            headers: disableScheduleCache
              ? {
                  ...headers,
                  "Cache-Control": "no-cache, no-store, must-revalidate",
                  Pragma: "no-cache",
                  Expires: "0",
                }
              : headers,
            signal: activeController.signal,
          });

          if (!response.ok) {
            throw new Error(`Schedule request failed with status ${response.status}`);
          }

          const data = await response.json();

          if (isMounted) {
            setScheduleData(data);
            setScheduleError("");
          }

          return;
        } catch (error) {
          if (activeController.signal.aborted) {
            return;
          }

          if (attempt === 2) {
            console.error("Error fetching schedule data:", error);

            if (isMounted) {
              setScheduleError(
                "Unable to load the latest schedule data from the server. Please refresh and try again."
              );
            }

            return;
          }
        }
      }
    };

    loadSchedule();

    return () => {
      isMounted = false;
      if (activeController) {
        activeController.abort();
      }
    };
  }, [disableScheduleCache, shouldUseRemoteSchedule]);

  // Show a loading state until data is fetched
  if (scheduleError) {
    return <div>{scheduleError}</div>;
  }

  if (!scheduleData) {
    return <div>Loading schedule data...</div>;
  }

  const { highlight, chapters = [], days = [] } = scheduleData;

  const handleOpenSession = (session, color) => {
    // Store the session along with the computed color
    setSelectedSession({ ...session, color });
  };

  const handleCloseModal = () => {
    setSelectedSession(null);
  };

  return (
    <div className="conference-schedule">
      <div className="conference-schedule-container">
        <div className="conference-highlight">
          <h1 className="main-h1">Program Schedule</h1>
          <p>
            <strong>Dates:</strong> {highlight.duration}
          </p>
          <p>
            <strong>Theme:</strong> {highlight.theme}
          </p>
          {highlight.description && <p>{highlight.description}</p>}
        </div>

        {/* Render chapters if available */}
        {chapters.length > 0 && (
          <div className="schedule-chapters">
            <h2 className="section-title">Conference Chapters</h2>
            {chapters.map((chapter, chapterIdx) => (
              <div className="day-wrapper" key={chapterIdx}>
                <h2 className="day-date">{chapter.title}</h2>
                <div className="day-sessions">
                  <div className="session-card" style={{ backgroundColor: sessionColors[chapterIdx % sessionColors.length] }}>
                    <div className="session-header">
                      <h3 className="session-title">{chapter.theme}</h3>
                      <p className="session-date-time">
                        <strong>Duration:</strong> {chapter.duration}
                      </p>
                    </div>
                    <button
                      className="view-details-btn"
                      onClick={() => handleOpenSession({ 
                        ...chapter, 
                        title: chapter.theme,
                        sessionTime: chapter.duration,
                        date: chapter.duration 
                      }, sessionColors[chapterIdx % sessionColors.length])}
                    >
                      View Chapter Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Render sessions grouped by day */}
        {days.length > 0 && (
          <div className="schedule-days">
            <h2 className="section-title">Main Conference</h2>
          {days.map((day, dayIdx) => (
            <div className="day-wrapper" key={dayIdx}>
              <h2 className="day-date">{day.date}</h2>
              <div className="day-sessions">
                {day.sessions.map((session, idx) => {
                  // Compute background color based on index
                  const bgColor = sessionColors[idx % sessionColors.length];
                  return (
                    <div
                      key={idx}
                      className="session-card"
                      style={{ backgroundColor: bgColor }}
                    >
                      <div className="session-header">
                        <h3 className="session-title">{session.title}</h3>
                        {session.department && (
                          <p className="session-department">
                            {session.department}
                          </p>
                        )}
                        <p className="session-date-time">
                          {session.date} | {session.sessionTime}
                        </p>
                      </div>
                      <button
                        className="view-details-btn"
                        onClick={() => handleOpenSession(session, bgColor)}
                      >
                        View More Details
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Modal Popup for session details */}
      {selectedSession && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>

            {/* Top accent strip using session color */}
            <div
              className="modal-accent"
              style={{ backgroundColor: selectedSession.color }}
            />

            {/* Header */}
            <div className="modal-header">
              <button
                className="modal-close"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <FaTimes />
              </button>
              {selectedSession.department && (
                <span className="modal-dept-badge">
                  {selectedSession.department}
                </span>
              )}
              <h3 className="modal-title">{selectedSession.title}</h3>
              <div className="modal-info-chips">
                {selectedSession.date && (
                  <span className="info-chip">
                    <FaCalendarAlt className="chip-icon" />
                    {selectedSession.date}
                  </span>
                )}
                {selectedSession.sessionTime && (
                  <span className="info-chip">
                    <FaClock className="chip-icon" />
                    {selectedSession.sessionTime}
                  </span>
                )}
              </div>
            </div>

            {/* Scrollable body */}
            <div className="modal-body">

              {/* Meta info */}
              {(selectedSession.venue ||
                selectedSession.chair ||
                selectedSession.coChair ||
                selectedSession.moderator) && (
                <div className="modal-meta-list">
                  {selectedSession.venue && (
                    <div className="meta-row">
                      <FaMapMarkerAlt className="meta-icon" />
                      <span className="meta-text">
                        <strong>Venue:</strong> {selectedSession.venue}
                      </span>
                    </div>
                  )}
                  {selectedSession.chair && (
                    <div className="meta-row">
                      <FaUserTie className="meta-icon" />
                      <span className="meta-text">
                        <strong>Chair:</strong> {selectedSession.chair}
                      </span>
                    </div>
                  )}
                  {selectedSession.coChair && (
                    <div className="meta-row">
                      <FaUsers className="meta-icon" />
                      <span className="meta-text">
                        <strong>Co-Chair:</strong> {selectedSession.coChair}
                      </span>
                    </div>
                  )}
                  {selectedSession.moderator && (
                    <div className="meta-row">
                      <FaChalkboardTeacher className="meta-icon" />
                      <span className="meta-text">
                        <strong>Moderator:</strong> {selectedSession.moderator}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Chapter sub-sessions */}
              {selectedSession.sessions &&
                selectedSession.sessions.length > 0 && (
                  <div className="modal-section">
                    <div className="section-label">
                      <FaListUl className="tl-icon" /> Sessions
                    </div>
                    {selectedSession.sessions.map((session, idx) => (
                      <div key={idx} className="sub-session-card">
                        <div className="sub-session-top">
                          <span className="sub-session-id">
                            {session.title}
                          </span>
                          {session.department && (
                            <span className="sub-session-dept">
                              {session.department}
                            </span>
                          )}
                        </div>
                        <div className="sub-session-chips">
                          {session.date && (
                            <span className="info-chip info-chip-sm">
                              <FaCalendarAlt className="chip-icon" />
                              {session.date}
                            </span>
                          )}
                          {session.sessionTime && (
                            <span className="info-chip info-chip-sm">
                              <FaClock className="chip-icon" />
                              {session.sessionTime}
                            </span>
                          )}
                        </div>
                        {session.venue && (
                          <div className="meta-row meta-row-sm">
                            <FaMapMarkerAlt className="meta-icon" />
                            <span className="meta-text">{session.venue}</span>
                          </div>
                        )}
                        {session.chair && (
                          <div className="meta-row meta-row-sm">
                            <FaUserTie className="meta-icon" />
                            <span className="meta-text">
                              <strong>Chair:</strong> {session.chair}
                            </span>
                          </div>
                        )}
                        {session.coChair && (
                          <div className="meta-row meta-row-sm">
                            <FaUsers className="meta-icon" />
                            <span className="meta-text">
                              <strong>Co-Chair:</strong> {session.coChair}
                            </span>
                          </div>
                        )}
                        {session.moderator && (
                          <div className="meta-row meta-row-sm">
                            <FaChalkboardTeacher className="meta-icon" />
                            <span className="meta-text">
                              <strong>Moderator:</strong> {session.moderator}
                            </span>
                          </div>
                        )}
                        {session.program && session.program.length > 0 && (
                          <div className="timeline-section">
                            <div className="timeline-label">
                              <FaListUl className="tl-icon" /> Program
                            </div>
                            <div className="timeline">
                              {session.program.map((talk, tIdx) => (
                                <div key={tIdx} className="timeline-item">
                                  <div className="timeline-dot" />
                                  <div className="timeline-content">
                                    {talk.time && (
                                      <span className="timeline-time">
                                        {talk.time}
                                      </span>
                                    )}
                                    <span className="timeline-talk">
                                      {talk.talkTitle}
                                    </span>
                                    {talk.speaker && (
                                      <span className="timeline-speaker">
                                        <FaUserMd className="spk-icon" />
                                        {talk.speaker}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

              {/* Direct program talks */}
              {selectedSession.program &&
                selectedSession.program.length > 0 && (
                  <div className="modal-section">
                    <div className="section-label">
                      <FaListUl className="tl-icon" /> Program
                    </div>
                    <div className="timeline">
                      {selectedSession.program.map((talk, idx) => (
                        <div key={idx} className="timeline-item">
                          <div className="timeline-dot" />
                          <div className="timeline-content">
                            {talk.time && (
                              <span className="timeline-time">{talk.time}</span>
                            )}
                            <span className="timeline-talk">
                              {talk.talkTitle}
                            </span>
                            {talk.speaker && (
                              <span className="timeline-speaker">
                                <FaUserMd className="spk-icon" />
                                {talk.speaker}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenceSchedule;
